import { CircleNotchIcon, PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminWorkspaces, type WorkspaceListItem } from "@/lib/api";

import { CreateWorkspaceDialog } from "./create-workspace-dialog";

export function AdminWorkspacesPage() {
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const workspacesQuery = useAdminWorkspaces(page);
  const data = workspacesQuery.data;
  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <h1 className="t-heading text-foreground">Workspaces</h1>
        <div className="flex items-center gap-2">
          <Link to="/admin/users">
            <Button variant="ghost">Users</Button>
          </Link>
          <Link to="/cockpit">
            <Button variant="outline">Back</Button>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted text-sm">
            One isolated workspace per client. Open one to add its client users and assign staff.
          </p>
          <Button onClick={() => setCreating(true)}>
            <PlusIcon className="h-4 w-4" />
            New workspace
          </Button>
        </div>
        {workspacesQuery.isLoading ? (
          <Card>
            <CardContent className="flex justify-center py-12">
              <CircleNotchIcon className="text-muted h-6 w-6 animate-spin" />
            </CardContent>
          </Card>
        ) : workspacesQuery.isError || !data ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted text-sm">
                Could not load workspaces. Please{" "}
                <button
                  type="button"
                  onClick={() => void workspacesQuery.refetch()}
                  className="underline"
                >
                  try again
                </button>
                .
              </p>
            </CardContent>
          </Card>
        ) : data.items.length === 0 ? (
          <Card>
            <CardContent className="space-y-2 py-12 text-center">
              <p className="text-foreground text-base">No workspaces yet.</p>
              <p className="text-muted text-sm">
                Create the first one with &ldquo;New workspace&rdquo;.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <WorkspaceTable workspaces={data.items} />
            {lastPage > 1 && (
              <div className="flex items-center justify-end gap-3 text-sm">
                <span className="text-muted">
                  Page {data.page} of {lastPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      <CreateWorkspaceDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function WorkspaceTable({ workspaces }: { workspaces: WorkspaceListItem[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/10 border-b">
            <th className="px-4 py-3 text-left font-medium">Name</th>
            <th className="px-4 py-3 text-left font-medium">Created</th>
            <th className="px-4 py-3 text-right font-medium">Engagements</th>
            <th className="px-4 py-3 text-right font-medium">Client users</th>
            <th className="px-4 py-3 text-right font-medium">Assigned staff</th>
          </tr>
        </thead>
        <tbody>
          {workspaces.map((ws) => (
            <tr key={ws.id} className="border-b last:border-0">
              <td className="px-4 py-3">
                <Link
                  to={`/admin/workspaces/${ws.id}`}
                  className="text-accent-text hover:underline"
                >
                  {ws.name}
                </Link>
              </td>
              <td className="text-muted px-4 py-3">
                {new Date(ws.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-3 text-right">{ws.engagement_count}</td>
              <td className="px-4 py-3 text-right">{ws.member_count}</td>
              <td className="px-4 py-3 text-right">{ws.assigned_staff_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
